export type ChipId = 'explainer' | 'walkthrough' | 'sales' | 'ad' | 'lesson' | 'scenario' | 'numbers' | 'shopper';

/** A starting idea under the prompt box. Picking one fills the box with its words and nothing else: Ivy picks the look, as for typed words. */
export interface Chip {
  id: ChipId;
  label: string;
  /** Interactive ideas sit in their own row, in words Ivy's routing sends to the interactive lesson pipeline. */
  kind: 'video' | 'interactive';
  prompt: string;
  /** The plan's lead line on /start. */
  reply: string;
  /** What the Plan step says it will make. */
  plan: string;
  scenes: number;
}

export const CHIPS: Chip[] = [
  {
    id: 'explainer',
    label: 'Explainer',
    kind: 'video',
    prompt: 'Explain how a CDN gets a web page to you in under a second',
    reply: 'The trip a request takes, one step at a time. Here’s how I’ll make it:',
    plan: '5 scenes, from your click to the server next door',
    scenes: 5,
  },
  {
    id: 'walkthrough',
    label: 'Product walkthrough',
    kind: 'video',
    prompt: 'Walk a new customer through their first five minutes in our app',
    reply: 'A friendly first-day tour. Here’s how I’ll make it:',
    plan: '5 scenes, one task each',
    scenes: 5,
  },
  {
    id: 'sales',
    label: 'Sales objection',
    kind: 'video',
    prompt: 'Handle the “it costs too much” objection for our sales team in 90 seconds',
    reply: 'A short scene your reps can learn from. Here’s how I’ll make it:',
    plan: '4 scenes: the objection, the reframe, the proof, the ask',
    scenes: 4,
  },
  {
    id: 'ad',
    label: 'Product ad',
    kind: 'video',
    prompt: 'A 30-second ad for a reusable water bottle, close up and bold',
    reply: 'One product, up close. Here’s how I’ll make it:',
    plan: '4 quick scenes with a strong last frame',
    scenes: 4,
  },
  {
    id: 'lesson',
    label: 'Interactive lesson',
    kind: 'interactive',
    prompt: 'A lesson where grade 6 learners place negative numbers on a number line',
    reply: 'A lesson that stops for the learner to try it. Here’s how I’ll make it:',
    plan: '6 scenes, 2 of them stop for the learner',
    scenes: 6,
  },
  {
    id: 'scenario',
    label: 'Training scenario',
    kind: 'interactive',
    prompt: 'A 90-second interactive training scenario for support agents: a customer is angry about a late refund, the learner picks what to say next, and the customer reacts to each choice',
    reply: 'A scene where the learner makes the call. Here’s how I’ll make it:',
    plan: '3 scenes: the complaint, the reply, how it lands',
    scenes: 3,
  },
  {
    id: 'numbers',
    label: 'Try the numbers',
    kind: 'interactive',
    prompt: 'A 2-minute interactive explainer where the viewer sets how much they save each month from age 25 and sees what it grows to by 65',
    reply: 'An explainer the viewer can change. Here’s how I’ll make it:',
    plan: '4 scenes, one where the viewer sets the numbers',
    scenes: 4,
  },
  {
    id: 'shopper',
    label: 'Shopper quiz',
    kind: 'interactive',
    prompt: 'A 90-second interactive shopping guide for a running shoe: the shopper answers three questions about how they run and sees the model that fits, ending at add to cart',
    reply: 'A guide that asks before it shows. Here’s how I’ll make it:',
    plan: '4 scenes: three questions, then the shoe that fits',
    scenes: 4,
  },
];

export const VIDEO_CHIPS = CHIPS.filter((c) => c.kind === 'video');
export const INTERACTIVE_CHIPS = CHIPS.filter((c) => c.kind === 'interactive');

export const chipById = (id: unknown): Chip | null => CHIPS.find((c) => c.id === id) ?? null;
